/**
 * Nuova segnalazione dal portale (QA26-F4.9): fonte (Salesforce è una delle fonti), tipo, impatto sul cliente, allegati
 * (mail .msg/.eml, PDF, foto, Office) caricati appena la segnalazione è creata. Il resto lo completa chi la prende in carico.
 */
import { App, Button, Card, Col, DatePicker, Form, Input, InputNumber, Row, Select, Space, Switch, Table } from "antd";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { Dayjs } from "dayjs";
import { FONTE_SALESFORCE, SEVERITA, VALUTE, type NuovaSegnalazioneInput, type SegnalazioneDto } from "@qa/shared";
import { SceltaAllegati, caricaAllegati } from "./scheda/Allegati";
import { api } from "../../api/client";
import { numFormatter, numParser } from "../../lib/format";
import { useAggiorna } from "../../lib/query";
import { messaggioErrore } from "../../i18n/errori";
import { SelectLista, useListe } from "../../components/campi";
import { PageHead, Sev } from "../../components/ui";

type Valori = Omit<NuovaSegnalazioneInput, "dataSegnalazione"> & { dataSegnalazione?: Dayjs | null };

export function CriteriSeverita() {
  const { t } = useTranslation();
  const l = useListe();
  return (
    <Card size="small" title={t("redflag.nuova.criteri")}>
      <Table
        size="small"
        showHeader={false}
        pagination={false}
        rowKey="severita"
        loading={l.isLoading}
        dataSource={l.data?.criteri ?? []}
        columns={[
          { key: "s", width: 100, render: (_, c) => <Sev s={c.severita} /> },
          { key: "t", dataIndex: "testo" },
        ]}
      />
    </Card>
  );
}

export default function NuovaSegnalazionePage() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const nav = useNavigate();
  const aggiorna = useAggiorna();
  const [form] = Form.useForm<Valori>();
  const [files, setFiles] = useState<File[]>([]);
  const fonte = Form.useWatch("fonte", form);
  const obbl = [{ required: true, whitespace: true, message: t("redflag.nuova.obbligatorio") }];
  const m = useMutation({
    mutationFn: async (v: Valori) => {
      const r = await api.post<SegnalazioneDto>("/redflag/segnalazioni", {
        ...v,
        fonteDettaglio: v.fonteDettaglio?.trim() || null,
        tipoSegnalazione: v.tipoSegnalazione ?? null,
        progetto: v.progetto?.trim() || null,
        impatto: v.impatto?.trim() || null,
        casoSalesforce: v.casoSalesforce?.trim() || null,
        poSap: v.poSap?.trim() || null,
        valoreImporto: v.valoreImporto ?? null,
        dataSegnalazione: v.dataSegnalazione ? v.dataSegnalazione.format("YYYY-MM-DD") : undefined,
      });
      // allegati subito dopo la creazione: se falliscono la segnalazione resta, si riprova dalla scheda
      if (files.length)
        await caricaAllegati(r.id, files).catch((e) => {
          message.error(`${t("redflag.nuova.allegatiErrore")} (${messaggioErrore(e)})`);
        });
      return r;
    },
    onSuccess: (r) => {
      message.success(t("redflag.nuova.inviata", { codice: r.codice }));
      void aggiorna();
      nav(`/redflag/segnalazioni/${r.id}`);
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  return (
    <>
      <PageHead
        crumbs={[{ label: t("portale.nome"), to: "/" }, { label: t("menu.gruppoRedflag"), to: "/redflag" }, { label: t("redflag.nuova.titolo") }]}
        title={t("redflag.nuova.titolo")}
        sub={t("redflag.nuova.sottotitolo")}
      />
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={16}>
          <Card>
            <Form<Valori> form={form} layout="vertical" initialValues={{ societa: "Brandart", severita: "HIGH", valuta: "EUR", urgente: false }} onFinish={(v) => m.mutate(v)}>
              <Row gutter={12}>
                <Col xs={24} md={8}>
                  <Form.Item name="fonte" label={t("redflag.nuova.fonte")} rules={[{ required: true, message: t("redflag.nuova.obbligatorio") }]}>
                    <SelectLista lista="fonte" placeholder={t("redflag.nuova.fontePh")} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={16}>
                  <Form.Item name="fonteDettaglio" label={t("redflag.nuova.fonteDettaglio")}>
                    <Input maxLength={300} placeholder={t("redflag.nuova.fonteDettaglioPh")} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col xs={24} md={8}>
                  <Form.Item name="tipoSegnalazione" label={t("redflag.nuova.tipoSegnalazione")}>
                    <SelectLista lista="tipo_segnalazione" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="societa" label={t("redflag.nuova.societa")} rules={obbl}>
                    <SelectLista lista="societa" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="cliente" label={t("redflag.nuova.cliente")} rules={obbl}>
                    <Input maxLength={200} placeholder={t("redflag.nuova.clientePh")} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="progetto" label={t("redflag.nuova.progetto")}>
                <Input maxLength={300} placeholder={t("redflag.nuova.progettoPh")} />
              </Form.Item>
              <Form.Item name="problema" label={t("redflag.nuova.problema")} rules={obbl}>
                <Input.TextArea rows={3} maxLength={5000} placeholder={t("redflag.nuova.problemaPh")} />
              </Form.Item>
              <Form.Item name="impatto" label={t("redflag.nuova.impatto")}>
                <Input.TextArea rows={2} maxLength={5000} placeholder={t("redflag.nuova.impattoPh")} />
              </Form.Item>
              <Row gutter={12}>
                <Col xs={12} md={6}>
                  <Form.Item name="severita" label={t("redflag.nuova.severita")} rules={[{ required: true, message: t("redflag.nuova.obbligatorio") }]}>
                    <Select options={SEVERITA.map((s) => ({ value: s, label: s }))} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item name="urgente" label={t("redflag.nuova.urgente")} valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
                <Col xs={16} md={9}>
                  <Form.Item name="valoreImporto" label={t("redflag.nuova.valore")}>
                    <InputNumber min={0} style={{ width: "100%" }} placeholder={t("redflag.nuova.valorePh")} formatter={numFormatter} parser={numParser} />
                  </Form.Item>
                </Col>
                <Col xs={8} md={5}>
                  <Form.Item name="valuta" label={t("redflag.nuova.valuta")}>
                    <Select options={VALUTE.map((v) => ({ value: v, label: v }))} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="casoSalesforce"
                    label={t("redflag.nuova.casoSf")}
                    rules={fonte === FONTE_SALESFORCE ? [{ required: true, whitespace: true, message: t("redflag.nuova.casoSfObbligatorio") }] : []}
                  >
                    <Input maxLength={40} placeholder={fonte === FONTE_SALESFORCE ? "00012345" : t("redflag.nuova.facoltativo")} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="poSap" label={t("redflag.nuova.poSap")}>
                    <Input maxLength={40} placeholder={t("redflag.nuova.facoltativo")} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="dataSegnalazione" label={t("redflag.nuova.data")} extra={t("redflag.nuova.dataNota")}>
                    <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" disabledDate={(d) => d.isAfter(new Date(), "day")} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label={t("redflag.nuova.allegati")} extra={t("redflag.allegati.dopoCreazione")}>
                <SceltaAllegati files={files} onChange={setFiles} />
              </Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={m.isPending}>
                  {t("redflag.nuova.invia")}
                </Button>
                <Link to="/redflag">
                  <Button>{t("azioni.annulla")}</Button>
                </Link>
              </Space>
            </Form>
          </Card>
        </Col>
        <Col xs={24} xl={8}>
          <CriteriSeverita />
        </Col>
      </Row>
    </>
  );
}
